import { createContext, createElement, useContext, useId, useState, type ReactNode } from 'react';

interface ExpansionPanelProps {
  /** Header title content (Angular `mat-panel-title`). */
  title: ReactNode;
  /** Controlled state (Angular `[expanded]`); leave undefined for uncontrolled. */
  expanded?: boolean;
  defaultExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  className?: string;
  children: ReactNode;
}

/** Panels inside an `Accordion` get `mat-expansion-panel-spacing` while expanded (`_hasSpacing()` in Material). */
const AccordionContext = createContext(false);

/**
 * `<mat-expansion-panel>`: the same elements, classes and attributes Material renders (host element, header with the
 * rotated indicator, `role="region"` content), so Material's own expansion styles and the ported rules apply. The body
 * is only rendered while expanded.
 */
export function ExpansionPanel({
  title,
  expanded,
  defaultExpanded = false,
  onExpandedChange,
  className = '',
  children,
}: ExpansionPanelProps) {
  const [internal, setInternal] = useState(defaultExpanded);
  const open = expanded ?? internal;
  const inAccordion = useContext(AccordionContext);
  const headerId = useId();
  const contentId = useId();

  const toggle = () => {
    if (expanded === undefined) setInternal(!open);
    onExpandedChange?.(!open);
  };

  return createElement(
    'mat-expansion-panel',
    {
      className:
        `mat-expansion-panel${open ? ' mat-expanded' : ''}${inAccordion && open ? ' mat-expansion-panel-spacing' : ''} ${className}`.trim(),
    },
    createElement(
      'mat-expansion-panel-header',
      {
        id: headerId,
        role: 'button',
        className: `mat-expansion-panel-header mat-focus-indicator${open ? ' mat-expanded' : ''} mat-expansion-toggle-indicator-after`,
        tabIndex: 0,
        'aria-controls': contentId,
        'aria-expanded': String(open),
        'aria-disabled': 'false',
        onClick: toggle,
        onKeyDown: (event: React.KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggle();
          }
        },
      },
      <span className="mat-content">
        {createElement('mat-panel-title', { className: 'mat-expansion-panel-header-title' }, title)}
      </span>,
      <span className="mat-expansion-indicator" style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}></span>,
    ),
    open && (
      <div role="region" className="mat-expansion-panel-content" id={contentId} aria-labelledby={headerId}>
        <div className="mat-expansion-panel-body">{children}</div>
      </div>
    ),
  );
}

/** `<mat-accordion [multi]>`: a container (each panel keeps its own state, i.e. multi = true). */
export function Accordion({ className = '', children }: { className?: string; children: ReactNode }) {
  return (
    <AccordionContext.Provider value>
      {createElement('mat-accordion', { className: `mat-accordion mat-accordion-multi ${className}`.trim() }, children)}
    </AccordionContext.Provider>
  );
}
